import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject, interval, of, takeUntil, tap, catchError } from 'rxjs';
import { SaveUser } from 'src/app/core-module/models/UserManagement/save-user';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';
import { NewUserWizardComponent } from '../new-user-wizard/new-user-wizard.component';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrl: './user-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserEditComponent implements OnInit, OnDestroy {
  public users: SaveUser[] = [];
  public filteredUsers: SaveUser[] = [];
  public userGroups: { label: string; items: SaveUser[] }[] = [];
  public userFilter: string = '';
  public selectedUser: SaveUser | null = null;
  public readOnlyMode: boolean = false;
  public loadingUsers: boolean = false;
  public loadError: boolean = false;
  public activeTab: number = 1;
  public externalChangeCheckLoading: boolean = false;
  private editableUserIds = new Set<number>();
  private destroy$ = new Subject<void>();
  @ViewChild(NewUserWizardComponent) private wizard?: NewUserWizardComponent;

  constructor(private UMS: UserManagementService, private toaster: ToastrService) {
    this.loadUsers();
  }

  ngOnInit() {
    interval(180000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.selectedUser || !this.wizard) {
          return;
        }
        this.externalChangeCheckLoading = this.wizard.isLoadingForStep(this.activeTab);
        if (this.externalChangeCheckLoading) {
          return;
        }
        this.wizard.checkForExternalChanges(this.activeTab);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUsers() {
    this.loadingUsers = true;
    this.loadError = false;
    this.UMS.getManageUsers(true)
      .pipe(
        tap((users: SaveUser[]) => {
          this.users = users.map(u => new SaveUser(u)).sort(this.sortUsers);
          this.editableUserIds = new Set(this.users.map(u => u.UserID));
          this.applyFilter();
          this.loadingUsers = false;
        }),
        catchError(() => {
          this.loadingUsers = false;
          this.loadError = true;
          this.toaster.addToastr(new Toastr({ Type: 3, Message: 'Unable to load users, please try again.' }));
          return of([]);
        })
      )
      .subscribe();
  }

  applyFilter() {
    if (!this.userFilter) {
      this.filteredUsers = this.users;
      this.buildUserGroups();
      return;
    }
    const search = this.userFilter.toLowerCase();
    this.filteredUsers = this.users.filter(u =>
      (u.DisplayName || '').toLowerCase().includes(search) ||
      (u.Username || '').toLowerCase().includes(search) ||
      (u.Email || '').toLowerCase().includes(search)
    );
    this.buildUserGroups();
  }

  private buildUserGroups() {
    this.userGroups = [
      { label: 'Active users', items: this.filteredUsers.filter(user => user.Active) },
      { label: 'Inactive users', items: this.filteredUsers.filter(user => !user.Active) }
    ].filter(group => group.items.length > 0);
  }

  getUserOptionLabel(user: SaveUser | null): string {
    if (!user) {
      return '';
    }
    return user.Active ? user.DisplayName : `${user.DisplayName} (Inactive)`;
  }

  selectUser(user: SaveUser | null) {
    if (!user) {
      this.selectedUser = null;
      this.readOnlyMode = false;
      return;
    }
    this.selectedUser = new SaveUser(user);
    this.readOnlyMode = !this.editableUserIds.has(this.selectedUser.UserID);
    this.activeTab = 1;
  }

  updateSelectedUser(user: SaveUser) {
    const updatedUser = new SaveUser(user);
    this.selectedUser = updatedUser;
    const index = this.users.findIndex(u => u.UserID === updatedUser.UserID);
    if (index >= 0) {
      this.users[index] = updatedUser;
      this.users.sort(this.sortUsers);
      this.applyFilter();
    }
  }

  clearSelection() {
    this.selectedUser = null;
    this.readOnlyMode = false;
  }

  setActiveTab(step: number) {
    if (this.activeTab === step) {
      return;
    }
    const previous = this.activeTab;
    this.activeTab = step;
    if (this.wizard) {
      this.wizard.flushPendingSaves(previous);
      this.externalChangeCheckLoading = this.wizard.isLoadingForStep(previous);
      if (!this.externalChangeCheckLoading) {
        this.wizard.checkForExternalChanges(previous);
      }
      this.wizard.onTabActivated(step);
    }
  }

  private loadEditableUsers() {
    this.UMS.getUserLocationUsers(false)
      .pipe(
        tap((users: BasicUser[]) => {
          this.editableUserIds = new Set(users.map(u => u.UserID));
          if (this.selectedUser) {
            this.readOnlyMode = !this.editableUserIds.has(this.selectedUser.UserID);
          }
        }),
        catchError(() => {
          this.toaster.addToastr(new Toastr({ Type: 3, Message: 'Unable to determine editable users.' }));
          return of([]);
        })
      )
      .subscribe();
  }

  private sortUsers(a: SaveUser, b: SaveUser): number {
    if (a.Active !== b.Active) {
      return a.Active ? -1 : 1;
    }
    return (a.DisplayName || '').localeCompare(b.DisplayName || '');
  }
}
